/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * CompanionAvatar
 * ----------------
 * A single entry point that renders the right avatar for a companion, based on
 * how that companion was created:
 *
 *   - Preset companions (e.g. the built-in Hiyori) ship a real Live2D model
 *     (`live2dModelUrl`). They render with <Live2DAvatar/> (Cubism 4 runtime).
 *   - User-uploaded companions never touch Live2D. Their portrait is a
 *     "paper-puppet": three auto-generated frames (neutral / mouth-open /
 *     eyes-closed) cross-faded by <PortraitAvatar/>. If frame generation failed,
 *     we still show the flat uploaded avatar as the neutral frame.
 *   - As a last resort (no avatar, no frames, no model) we fall back to a
 *     bundled neutral portrait so the stage is never empty.
 *
 * This keeps the two rendering paths (heavy Live2D vs. light portrait) behind
 * one prop-compatible surface, so callers (Room / Antechamber) don't branch.
 */

import { CSSProperties } from 'react';
import Live2DAvatar from '@/components/Live2DAvatar';
import PortraitAvatar, { PortraitAvatarFrames, ContentRect } from '@/components/PortraitAvatar';

// Bundled last-resort frames, used only when a companion has no avatar, no
// generated frames and no Live2D model, so the stage is never empty. Uses a
// dedicated high-res default portrait with its own matted + aligned mouthOpen /
// eyesClosed frames (built by scripts/rebuild-default-portrait.py from
// 默认立绘.png), so the fallback blinks and talks like a real companion instead
// of showing a low-res static headshot.
const FALLBACK_PORTRAIT_FRAMES: PortraitAvatarFrames = {
  neutral:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260723/avatar/bb7e1acb-6506-431b-a060-b45394970534.png',
  mouthOpen:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260723/frame/4ce956e8-7d7e-47cc-aec1-784cdc3e74e7.png',
  eyesClosed:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260723/frame/b0f73e7e-147f-41be-b0ac-72ac1ceecf53.png',
};

// Mobile fallback for the Live2D preset (Hiyori). The Cubism runtime is heavy
// and unreliable on mobile GPUs/WebGL — the model can silently fail to load or
// its ticker gets throttled, leaving a frozen figure. So on mobile we skip
// Live2D and animate a paper-puppet built from a matted full-body portrait
// (neutral + mouth-open + eyes-closed) generated through the in-app avatar
// pipeline from hiyori_transparent.png via <PortraitAvatar/> instead. Hiyori is
// the only live2dModelUrl preset, so a single frame set covers the
// disableLive2d path.
const LIVE2D_MOBILE_FALLBACK_FRAMES: PortraitAvatarFrames = {
  neutral:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/avatar/e8034664-defa-4789-a3f9-f4339f18a8bd.png',
  mouthOpen:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/62c953e9-15f6-4a6a-8384-c963662287c4.png',
  eyesClosed:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/491e91a5-c9da-4276-9a27-7406a9ef1d80.png',
};

export interface CompanionAvatarSource {
  /** Real Live2D *.model3.json URL. Present only for preset companions. */
  live2dModelUrl?: string;
  /** Flat uploaded avatar (also used as the neutral frame for the puppet). */
  avatar?: string;
  /** Auto-generated animation frames for user-uploaded companions. */
  portraitFrames?: {
    mouthOpen?: string;
    eyesClosed?: string;
  };
}

function isLikelyTransparentFrame(url: string) {
  const value = String(url || '').trim();
  if (!value) return false;
  const normalized = value.split(/[?#]/)[0].toLowerCase();
  return (
    value.startsWith('data:image/png') ||
    value.startsWith('data:image/webp') ||
    normalized.endsWith('.png') ||
    normalized.endsWith('.webp')
  );
}

interface CompanionAvatarProps extends CompanionAvatarSource {
  /** Whether the companion is currently speaking (drives lip-sync). */
  isTalking?: boolean;
  /** RTC linear volume (0..255); only used while talking. */
  volume?: number;
  className?: string;
  style?: CSSProperties;
  fit?: 'cover' | 'contain';
  zoom?: number;
  /** object-position for the puppet frames. Defaults to a top-anchored stage
   *  crop; pass 'center' to center-crop like the dock thumbnail. */
  objectPosition?: string;
  alt?: string;
  /** When true, skip the heavy/unreliable Live2D path and render the preset's
   *  paper-puppet fallback instead. Set on mobile, where Cubism/WebGL is flaky. */
  disableLive2d?: boolean;
  /** Reports the rendered character rect (fractions of the container) so callers
   *  can anchor overlays (e.g. a resize handle) to the visible character. */
  onContentRect?: (rect: ContentRect) => void;
}

/**
 * Build the paper-puppet frame set for a user-uploaded companion. Uses the
 * uploaded avatar as the neutral base and, when available, the generated
 * mouth-open / eyes-closed variants. Returns null when there is no avatar at
 * all, so the caller can fall back to the bundled neutral portrait.
 */
export function buildCompanionFrames(source: CompanionAvatarSource): PortraitAvatarFrames | null {
  const avatar = source.avatar || '';
  if (!avatar) return null;
  const mouthOpen = source.portraitFrames?.mouthOpen || '';
  const eyesClosed = source.portraitFrames?.eyesClosed || '';
  return {
    neutral: avatar,
    // Do not fake a mouth-open layer with the neutral frame: it only creates a
    // whole-avatar "bubble" feeling without real lip movement.
    mouthOpen: isLikelyTransparentFrame(mouthOpen) ? mouthOpen : undefined,
    eyesClosed: isLikelyTransparentFrame(eyesClosed) ? eyesClosed : undefined,
  };
}

function CompanionAvatar({
  live2dModelUrl,
  avatar,
  portraitFrames,
  isTalking = false,
  volume = 0,
  className,
  style,
  fit = 'contain',
  zoom = 1,
  objectPosition,
  alt = '',
  disableLive2d = false,
  onContentRect,
}: CompanionAvatarProps) {
  // Preset companions with a real Live2D model take the Cubism path — except on
  // mobile (disableLive2d), where WebGL/Cubism is flaky and the model can freeze.
  // There we fall through to the paper-puppet using a dedicated full-body frame
  // set so Hiyori still blinks / lip-syncs.
  if (live2dModelUrl && !disableLive2d) {
    return (
      <Live2DAvatar
        modelUrl={live2dModelUrl}
        isTalking={isTalking}
        volume={volume}
        className={className}
        style={style}
        fit={fit}
        zoom={zoom}
        onContentRect={onContentRect}
      />
    );
  }

  // Otherwise render the paper-puppet. A Live2D preset on the disabled path uses
  // its dedicated full-body frame set (its `avatar` is only a headshot, unusable
  // as a stage portrait). Everyone else uses uploaded frames or the neutral fallback.
  const frames =
    live2dModelUrl && disableLive2d
      ? LIVE2D_MOBILE_FALLBACK_FRAMES
      : buildCompanionFrames({ avatar, portraitFrames }) || FALLBACK_PORTRAIT_FRAMES;
  return (
    <PortraitAvatar
      frames={frames}
      isTalking={isTalking}
      volume={volume}
      className={className}
      style={style}
      fit={fit}
      zoom={zoom}
      objectPosition={objectPosition}
      alt={alt}
      onContentRect={onContentRect}
    />
  );
}

export default CompanionAvatar;
